/**
 * /surveys (tracker item 69): the answers customers sent, newest first, with
 * filters (dates, technician, rating, low scores only), the rating, the
 * comment, and links to the customer and the job. Low scores are highlighted
 * (a border and a label, not colour alone). The comment is untrusted text and
 * is rendered as plain text. ADMIN and OFFICE_MANAGER.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canViewSurveys } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { StarDisplay } from '../../components/StarRating';
import { inputClass, labelClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { dateParam, dateRangeError, pageParam } from '../reports/reportUi';
import { RATING_LABELS, RATINGS, SATISFACTION_REPORT_PATH, SURVEY_SETTINGS_PATH, ratingLabel, ratingParam, surveyDate } from './surveyUi';

const PAGE_SIZE = 20;

export function SurveysInboxPage() {
  const { user } = useAuth();
  const api = useMemo(() => createAPIClient(), []);
  const allowed = canViewSurveys(user);
  const [params, setParams] = useSearchParams();
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const technicianId = params.get('technicianId') || '';
  const rating = ratingParam(params.get('rating'));
  const lowOnly = params.get('lowOnly') === 'true';
  const page = pageParam(params.get('page'));
  const rangeError = dateRangeError(startDate, endDate);

  const [technicians, setTechnicians] = useState<types.UserListItem[]>([]);
  const [result, setResult] = useState<types.SurveyListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const change = useCallback((next: Record<string, string>) => {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    if (!('page' in next)) updated.delete('page');
    setParams(updated);
  }, [params, setParams]);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    // Include inactive users so a former technician's answers can still be filtered
    api.listUsers(true).then((users) => { if (active) setTechnicians(users.filter((u) => u.role !== types.UserRole.CUSTOMER_PORTAL)); }).catch(() => { /* the filter just stays empty */ });
    return () => { active = false; };
  }, [api, allowed]);

  const queryKey = JSON.stringify({ startDate, endDate, technicianId, rating, lowOnly, page });
  useEffect(() => {
    if (!allowed || rangeError) { setLoading(false); setResult(null); return undefined; }
    let active = true;
    setLoading(true);
    setError(null);
    api.listSurveyResponses({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      technicianId: technicianId || undefined,
      rating: rating ?? undefined,
      lowOnly: lowOnly || undefined,
      page,
      limit: PAGE_SIZE,
    })
      .then((data) => { if (active) { setResult(data); setLoading(false); } })
      .catch((cause) => { if (active) { setError(getErrorMessage(cause, 'Could not load the survey answers')); setLoading(false); } });
    return () => { active = false; };
    // queryKey carries every input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, allowed, queryKey, rangeError]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">The survey inbox is available to admins and office managers.</p>;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer surveys</h1>
          <p className="mt-1 text-sm text-gray-600">What customers said about finished visits.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={SATISFACTION_REPORT_PATH} className={`${secondaryButtonClass} inline-flex items-center`}>Satisfaction report</Link>
          <Link to={SURVEY_SETTINGS_PATH} className={`${secondaryButtonClass} inline-flex items-center`}>Settings</Link>
        </div>
      </div>

      <section aria-label="Filters" className="mt-5 grid grid-cols-1 gap-4 rounded-lg bg-white p-4 shadow sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="survey-from" className={labelClass}>Answered from</label>
          <input id="survey-from" type="date" className={inputClass} value={startDate} onChange={(e) => change({ startDate: e.target.value })} />
        </div>
        <div>
          <label htmlFor="survey-to" className={labelClass}>Answered to</label>
          <input id="survey-to" type="date" className={inputClass} value={endDate} onChange={(e) => change({ endDate: e.target.value })} />
        </div>
        <div>
          <label htmlFor="survey-technician" className={labelClass}>Technician</label>
          <select id="survey-technician" className={inputClass} value={technicianId} onChange={(e) => change({ technicianId: e.target.value })}>
            <option value="">All technicians</option>
            {technicianId && !technicians.some((t) => t.id === technicianId) && <option value={technicianId}>Selected technician</option>}
            {technicians.map((t) => <option key={t.id} value={t.id}>{t.email}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="survey-rating" className={labelClass}>Rating</label>
          <select id="survey-rating" className={inputClass} value={rating === null ? '' : String(rating)} onChange={(e) => change({ rating: e.target.value })}>
            <option value="">Any rating</option>
            {RATINGS.map((n) => <option key={n} value={n}>{n} - {RATING_LABELS[n]}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-4">
          <input id="survey-low-only" type="checkbox" className="h-5 w-5" checked={lowOnly} onChange={(e) => change({ lowOnly: e.target.checked ? 'true' : '' })} />
          <label htmlFor="survey-low-only" className="text-sm text-gray-800">
            Low scores only{result ? ` (${result.threshold} star${result.threshold === 1 ? '' : 's'} or fewer)` : ''}
          </label>
        </div>
      </section>

      {rangeError && <div className="mt-4"><Toast kind="warning">{rangeError}</Toast></div>}
      {error && (
        <div className="mt-4">
          <Toast kind="error">{error}</Toast>
        </div>
      )}
      {loading && <p role="status" className="mt-6 text-center text-gray-600">Loading answers…</p>}

      {!loading && result && (
        <>
          <p className="mt-4 text-sm text-gray-600" aria-live="polite">
            {result.pagination.total === 0 ? 'No answers match these filters.' : `${result.pagination.total} answer${result.pagination.total === 1 ? '' : 's'}`}
          </p>
          <ul className="mt-3 space-y-3" data-testid="survey-list">
            {result.data.map((item) => (
              <li
                key={item.id}
                className={`rounded-lg border-2 bg-white p-4 shadow-sm ${item.lowScore ? 'border-red-300' : 'border-transparent'}`}
                data-testid="survey-row"
                data-low={item.lowScore ? 'true' : 'false'}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <StarDisplay rating={item.rating} className="text-xl" />
                  <span className="text-sm font-semibold text-gray-900">{item.rating} - {ratingLabel(item.rating)}</span>
                  {item.lowScore && <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800">Low score: follow up</span>}
                  <span className="ml-auto text-xs text-gray-500">{new Date(item.createdAt).toLocaleString()}</span>
                </div>
                {item.comment ? (
                  <blockquote className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800">{item.comment}</blockquote>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">No comment.</p>
                )}
                <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <Link to={`/customers/${encodeURIComponent(item.customer.id)}`} className="font-medium text-blue-700 hover:underline break-words">{item.customer.name}</Link>
                  <Link to={`/jobs/${encodeURIComponent(item.job.id)}`} className="text-blue-700 hover:underline">
                    Job of {surveyDate(item.job.completedDate ?? item.job.scheduledDate)}
                  </Link>
                  <span className="text-gray-600">Technician: {item.technician ? item.technician.name : 'Unassigned'}</span>
                </p>
              </li>
            ))}
          </ul>
          {result.pagination.pages > 1 && (
            <nav aria-label="Pages" className="mt-4 flex items-center justify-between">
              <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => change({ page: page > 2 ? String(page - 1) : '' })}>Previous</button>
              <span className="text-sm text-gray-600">Page {result.pagination.page} of {result.pagination.pages}</span>
              <button type="button" className={secondaryButtonClass} disabled={page >= result.pagination.pages} onClick={() => change({ page: String(page + 1) })}>Next</button>
            </nav>
          )}
        </>
      )}
    </main>
  );
}
